import React, { useCallback, useEffect, useState } from "react";
import { isAnyRDT, isCoworkerHubRDT } from "@coworker/reusable/helpers/devices";
import { isBarcodeDetectorSupported } from "./pages/default-scanner-page/defaultScannerPageUtils";
import { CameraScannerComponent } from "./pages/camera-scanner-page/CameraScannerComponent";
import { SearchItemCardRenderType } from "./types/SearchItemCardRenderType";
import { ProductSearchComponent } from "./pages/product-search-page/ProductSearchComponent";
import { Item } from "../shared/types/item";
import { detectBarcodeFormat } from "./pages/hdi-scanner-page/hdiScannerPageUtils";
import { extractBasedOnSymbology } from "./helpers/extractBasedOnSymbologyUtils";
import { ExtractedArticleInformationType } from "./types/ExtractedArticleInformationType";
import { SymbologyType } from "./types/SymbologyType";
import { useHDIScanner } from "./hooks/useHDIScanner";
import useCoworkerHubScanner from "./hooks/useCoworkerHubScanner";
import CameraIconPath from "../shared/wrappers/FixaSSRIcon/paths/CameraIconPath";
import BarcodeIconPath from "../shared/wrappers/FixaSSRIcon/paths/BarcodeIconPath";
import FixaSSRIcon from "../shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import { useTranslation } from "@coworker/locales";
import FixaStatus from "../shared/wrappers/FixaStatus/FixaStatus";
import FixaInlineMessage from "../shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import InformationCircleIconPath from "../shared/wrappers/FixaSSRIcon/paths/InformationCircleIconPath";
import { ScanResult } from "./hooks/useDetectBarcode";
import styles from "./default-scanner-component.module.css";

interface DefaultScannerComponentProps {
  onResult: (result: ExtractedArticleInformationType) => void;
  shouldHideSPRItems?: boolean;
  shouldHideLocalCombinationItems?: boolean;
  isCameraOpen: boolean;
  onCameraOpenChange: (isCameraOpen: boolean) => void;
  shouldShowLargeReadyToScan?: boolean;
  withSearchPadding?: boolean;
}

const DefaultScannerComponent: React.FC<DefaultScannerComponentProps> = ({
  onResult,
  shouldHideSPRItems = false,
  shouldHideLocalCombinationItems = false,
  isCameraOpen,
  onCameraOpenChange,
  withSearchPadding = false,
  shouldShowLargeReadyToScan = false,
}) => {
  const isBarcodeDetectorAvailable = isBarcodeDetectorSupported();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchIsActive, setSearchIsActive] = useState(false);
  const { t } = useTranslation();

  const handleCameraDetected = useCallback(
    (scanResult: ScanResult) => {
      const symbologyFormat = detectBarcodeFormat(scanResult.barcode);
      const extracted = extractBasedOnSymbology(
        symbologyFormat,
        scanResult.barcode
      );
      onResult(extracted);
      onCameraOpenChange(false);
    },
    [onResult, onCameraOpenChange]
  );

  const handleItemCardClick = useCallback(
    (item: Item) => {
      const extracted = extractBasedOnSymbology("ARTICLE_NUMBER", item.no);
      setSearchQuery("");
      onResult(extracted);
    },
    [onResult]
  );

  /** Background handler: HDI keyboard-wedge scanner */
  const handleHDIScanner = useCallback(
    (data: string, symbologyFormat: SymbologyType) => {
      if (!isAnyRDT()) {
        return;
      }
      const extracted = extractBasedOnSymbology(symbologyFormat, data);
      onResult(extracted);
    },
    [onResult]
  );

  /** Background handler: Coworker Hub postMessage scanner */
  const handleCoworkerHubScanner = useCallback(
    (data: string, symbologyFormat: SymbologyType) => {
      if (!isCoworkerHubRDT()) {
        return;
      }
      const extracted = extractBasedOnSymbology(symbologyFormat, data);
      onResult(extracted);
    },
    [onResult]
  );

  /** Background hook: HDI keyboard-wedge scanner */
  useHDIScanner(handleHDIScanner);

  /** Background hook: Coworker Hub postMessage scanner */
  useCoworkerHubScanner(handleCoworkerHubScanner);

  const hasHardwareScanner = isAnyRDT() || isCoworkerHubRDT();

  useEffect(() => {
    if (hasHardwareScanner && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [hasHardwareScanner]);

  const showReadyToScan =
    hasHardwareScanner && !searchIsActive && searchQuery.length === 0;
  if (isCameraOpen) {
    return (
      <CameraScannerComponent
        onDetected={handleCameraDetected}
        handleSearchClick={() => onCameraOpenChange(false)}
      />
    );
  }
  const scannerContent = (
    <>
      <ProductSearchComponent
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        handleItemCardClick={handleItemCardClick}
        itemCardRenderType={SearchItemCardRenderType.button}
        shouldHideSPRItems={shouldHideSPRItems}
        shouldHideLocalCombinationItems={shouldHideLocalCombinationItems}
        autoFocus={!hasHardwareScanner}
        shouldHideHistory={showReadyToScan}
        shouldHideResults={showReadyToScan}
        onSearchFocus={() => setSearchIsActive(true)}
        onSearchBlur={() => {
          if (searchQuery.length === 0) setSearchIsActive(false);
        }}
        searchBarActions={
          isBarcodeDetectorAvailable
            ? [
                {
                  ssrIcon: CameraIconPath,
                  onClick: () => onCameraOpenChange(true),
                },
              ]
            : undefined
        }
      />
      {showReadyToScan && (
        <div className={styles["container"]}>
          {!shouldShowLargeReadyToScan && (
            <FixaStatus
              label={t("scannerReadyString")}
              variant="positive"
              small
            />
          )}
          {shouldShowLargeReadyToScan && (
            <div>
              <FixaInlineMessage
                variant="informative"
                subtle
                ssrIcon={InformationCircleIconPath}
                body={t("scannerFreeformTipString")}
              />
              <div className={styles["readyToScanContainer"]}>
                <FixaSSRIcon paths={BarcodeIconPath} />
                <h4>{t("readyToScanString")}</h4>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );

  return withSearchPadding ? (
    <div className={styles["searchPaddingWrapper"]}>{scannerContent}</div>
  ) : (
    scannerContent
  );
};

export default DefaultScannerComponent;
